import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Zap, ArrowLeft, Cookie, Shield, BarChart3, Settings } from 'lucide-react';
import { toast } from 'sonner';

const Cookies = () => {
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: true,
    marketing: false,
    personalization: true
  });

  useEffect(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem('cookiePreferences');
    if (saved) {
      setPreferences(JSON.parse(saved));
    }
  }, []);

  const savePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    toast.success('Cookie preferences saved!');
  };

  const acceptAll = () => {
    const allEnabled = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setPreferences(allEnabled);
    localStorage.setItem('cookiePreferences', JSON.stringify(allEnabled));
    toast.success('All cookies accepted!');
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    toast.success('Non-essential cookies rejected!');
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-white/10 bg-void-paper/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neon-mint rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">TrafficFlow <span className="text-neon-mint">AI</span></span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-neon-mint/10 rounded-full flex items-center justify-center">
            <Cookie className="w-6 h-6 text-neon-mint" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white">Cookie Policy</h1>
            <p className="text-gray-400">Last updated: January 24, 2026</p>
          </div>
        </div>

        {/* Cookie Settings Card */}
        <Card className="bg-gradient-to-r from-neon-mint/10 to-neon-violet/10 border-neon-mint/30 mb-8">
          <CardHeader>
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <Settings className="w-5 h-5 text-neon-mint" />
              Cookie Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Essential Cookies */}
            <div className="flex items-center justify-between p-4 bg-void rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-gray-400" />
                <div>
                  <h4 className="text-white font-medium">Essential Cookies</h4>
                  <p className="text-gray-500 text-sm">Required for the website to function properly</p>
                </div>
              </div>
              <Switch checked={true} disabled className="opacity-50" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-void rounded-lg">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-gray-400" />
                <div>
                  <h4 className="text-white font-medium">Analytics Cookies</h4>
                  <p className="text-gray-500 text-sm">Help us understand how visitors use our site</p>
                </div>
              </div>
              <Switch 
                checked={preferences.analytics} 
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                className="data-[state=checked]:bg-neon-mint"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-void rounded-lg">
              <div className="flex items-center gap-3">
                <Cookie className="w-5 h-5 text-gray-400" />
                <div>
                  <h4 className="text-white font-medium">Marketing Cookies</h4>
                  <p className="text-gray-500 text-sm">Used to deliver relevant advertisements</p>
                </div>
              </div>
              <Switch 
                checked={preferences.marketing} 
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, marketing: checked }))}
                className="data-[state=checked]:bg-neon-mint"
              />
            </div>

            {/* Personalization Cookies */}
            <div className="flex items-center justify-between p-4 bg-void rounded-lg">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-gray-400" />
                <div>
                  <h4 className="text-white font-medium">Personalization Cookies</h4>
                  <p className="text-gray-500 text-sm">Remember your preferences and settings</p>
                </div>
              </div>
              <Switch 
                checked={preferences.personalization} 
                onCheckedChange={(checked) => setPreferences(p => ({ ...p, personalization: checked }))}
                className="data-[state=checked]:bg-neon-mint"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4">
              <Button onClick={savePreferences} className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold">
                Save Preferences
              </Button>
              <Button onClick={acceptAll} variant="outline" className="border-white/20 text-white hover:bg-white/5">
                Accept All
              </Button>
              <Button onClick={rejectNonEssential} variant="ghost" className="text-gray-400 hover:text-white">
                Reject Non-Essential
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cookie Policy Content */}
        <div className="prose prose-invert max-w-none space-y-8 text-gray-300">
          <section>
            <h2 className="font-heading text-xl text-white mb-4">What Are Cookies?</h2>
            <p>
              Cookies are small text files that are stored on your device when you visit a website. They help the website remember your preferences and understand how you use the site.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">Types of Cookies We Use</h2>
            
            <h3 className="font-semibold text-white mt-4 mb-2">Essential Cookies</h3>
            <p>These cookies are necessary for the website to function and cannot be disabled. They include:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Authentication cookies to keep you logged in</li>
              <li>Security cookies to prevent fraud</li>
              <li>Session cookies for maintaining your session</li>
            </ul>

            <h3 className="font-semibold text-white mt-4 mb-2">Analytics Cookies</h3>
            <p>These cookies help us understand how visitors interact with our website:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Pages visited and time spent</li>
              <li>Traffic sources and user journey</li>
              <li>Error tracking and performance monitoring</li>
            </ul>

            <h3 className="font-semibold text-white mt-4 mb-2">Marketing Cookies</h3>
            <p>These cookies are used to deliver relevant advertisements:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Track ad effectiveness</li>
              <li>Retargeting and remarketing</li>
              <li>Social media integration</li>
            </ul>

            <h3 className="font-semibold text-white mt-4 mb-2">Personalization Cookies</h3>
            <p>These cookies remember your preferences:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Language and region settings</li>
              <li>Display preferences (e.g., dark mode)</li>
              <li>Recently viewed content</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">Third-Party Cookies</h2>
            <p>We may use third-party services that set their own cookies:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
              <li><strong>Analytics providers:</strong> Usage statistics and performance monitoring</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">Managing Cookies</h2>
            <p>
              You can manage your cookie preferences using the controls above. You can also control cookies through your browser settings. Note that disabling certain cookies may affect your experience on our website.
            </p>
            <p className="mt-2">
              Most browsers allow you to:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>View cookies stored on your device</li>
              <li>Delete individual or all cookies</li>
              <li>Block cookies from specific or all websites</li>
              <li>Set preferences for first-party vs third-party cookies</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-white mb-4">Contact Us</h2>
            <p>
              If you have questions about our use of cookies, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> <a href="mailto:support@trafficflowai.com" className="text-neon-mint hover:underline">support@trafficflowai.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/cookies" className="hover:text-white transition-colors text-neon-mint">Cookie Policy</Link>
          <Link to="/support" className="hover:text-white transition-colors">Support</Link>
        </div>
      </footer>
    </div>
  );
};

export default Cookies;
